// Full character sheet rendering — used both as the live builder preview and
// the in-play character drawer. When `actions` is provided, the sheet becomes
// interactive: equip/use items, cast spells (spending slots), restore slots.
import { useState } from "react";
import type { FullCharacter, SpellEntry } from "../lib/types";
import { ABILITY_KEYS } from "../lib/types";
import { ABILITY_ABBR, signed, titleizeSlug } from "../lib/format";
import { SKILL_LABELS, SKILL_DESC, SKILL_ABILITY } from "../lib/srd";
import { PixelArt } from "./PixelArt";

export interface SheetActions {
  busy: boolean;
  onEquip: (index: string, name: string, equipped: boolean) => void;
  onUseItem: (index: string, name: string) => void;
  onCast: (spellIndex: string, level: number, name: string) => void;
  onRestoreSlot: (level: number) => void;
  onRemoveItem: (index: string, name: string) => void;
  onAttune?: (index: string, name: string, attuned: boolean) => void;
}

const CONSUMABLE_HINTS = ["potion", "scroll", "elixir", "oil", "dust", "philter"];
function isConsumable(name: string): boolean {
  const n = name.toLowerCase();
  return CONSUMABLE_HINTS.some((h) => n.includes(h));
}

export function CharacterSheet({
  ch, actions, gameId, art = {},
}: {
  ch: FullCharacter;
  actions?: SheetActions;
  gameId?: string;
  art?: Record<string, string>;
}) {
  const slotLevels = Object.keys(ch.spell_slots)
    .map(Number)
    .sort((a, b) => a - b);
  const spellsByLevel = groupSpells(ch);

  return (
    <div className="sheet">
      <div className="sheet__name">{ch.name}</div>
      <div className="sheet__sub">
        {titleizeSlug(ch.subrace || ch.race)} ·{" "}
        {ch.classes
          .map((c) => `${titleizeSlug(c.class)}${c.subclass ? ` (${titleizeSlug(c.subclass)})` : ""} ${c.level}`)
          .join(" / ")}{" "}
        · {titleizeSlug(ch.alignment)}
      </div>

      <div className="sheet__topstats">
        <div className="bigstat"><div className="v">{ch.ac}</div><div className="k">Armor Class</div></div>
        <div className="bigstat"><div className="v">{ch.max_hp}</div><div className="k">Max HP</div></div>
        <div className="bigstat"><div className="v">{signed(ch.initiative)}</div><div className="k">Initiative</div></div>
        <div className="bigstat"><div className="v">{ch.speed ?? 30}</div><div className="k">Speed</div></div>
        <div className="bigstat"><div className="v">{signed(ch.proficiency_bonus)}</div><div className="k">Prof Bonus</div></div>
      </div>

      <div className="sheet__abilities">
        {ABILITY_KEYS.map((ab) => {
          const bd = ch.score_breakdown?.[ab];
          const tip = bd
            ? `Base ${bd.base}` +
              (bd.racial ? ` · racial ${bd.racial >= 0 ? "+" : ""}${bd.racial}` : "") +
              (bd.item ? ` · item ${bd.item >= 0 ? "+" : ""}${bd.item}` : "") +
              ` = ${bd.total}`
            : undefined;
          return (
            <div key={ab} className={`ability-cell${ch.save_proficiencies.includes(ab) ? " prof" : ""}`} title={tip}>
              <div className="ab">{ABILITY_ABBR[ab]}</div>
              <div className="mod">{signed(ch.modifiers[ab])}</div>
              <div className="raw">
                {ch.scores[ab]}
                {bd && (bd.racial || bd.item) ? <span className="raw-layers">{bd.base}{bd.racial ? `+${bd.racial}` : ""}{bd.item ? `+${bd.item}` : ""}</span> : null}
              </div>
            </div>
          );
        })}
      </div>

      <div className="sheet__block">
        <div className="label">Saving Throws</div>
        <div className="tag-row">
          {ABILITY_KEYS.map((ab) => (
            <span key={ab} className={`chip${ch.save_proficiencies.includes(ab) ? " chip--gold" : ""}`}>
              {ABILITY_ABBR[ab]} {signed(ch.saving_throws[ab] ?? 0)}
            </span>
          ))}
        </div>
      </div>

      <div className="sheet__block">
        <div className="row-between">
          <div className="label" style={{ margin: 0 }}>Skills</div>
          <span className="muted" style={{ fontSize: 12 }}>Passive Perception {ch.passive_perception}</span>
        </div>
        <div className="skill-list mt8">
          {Object.entries(SKILL_LABELS).map(([key, label]) => (
            <SkillRow
              key={key}
              skillKey={key}
              label={label}
              mod={ch.skills[key] ?? 0}
              prof={ch.skill_proficiencies.includes(key)}
              expert={ch.skill_expertise.includes(key)}
            />
          ))}
        </div>
      </div>

      {ch.spell_save_dc != null && (
        <div className="sheet__block">
          <div className="label">Spellcasting</div>
          <div className="tag-row mt8">
            <span className="chip chip--gold">Save DC {ch.spell_save_dc}</span>
            <span className="chip">Atk {signed(ch.spell_attack_bonus ?? 0)}</span>
            {ch.spell_ability && <span className="chip">{ABILITY_ABBR[ch.spell_ability]}</span>}
          </div>
          {slotLevels.length > 0 && (
            <div className="mt12" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {slotLevels.map((lvl) => {
                const total = ch.spell_slots[String(lvl)] ?? 0;
                const used = ch.spell_slots_used[String(lvl)] ?? 0;
                return (
                  <div className="slot-line" key={lvl}>
                    <span className="lvl">L{lvl}</span>
                    <span className="pips">
                      {Array.from({ length: total }).map((_, i) => {
                        const isOpen = i < total - used;
                        return (
                          <span
                            key={i}
                            className={`pip ${isOpen ? "open" : "used"}${actions && !isOpen ? " pip--restore" : ""}`}
                            title={actions && !isOpen ? "Restore this slot" : undefined}
                            onClick={() => { if (actions && !isOpen && !actions.busy) actions.onRestoreSlot(lvl); }}
                          />
                        );
                      })}
                    </span>
                    {actions && (
                      <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>
                        {total - used}/{total}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {(ch.subclass_features?.length > 0 || ch.feats?.length > 0 || (ch.race_traits?.length ?? 0) > 0) && (
        <div className="sheet__block">
          <div className="label">Features &amp; Feats</div>
          {ch.race_traits?.map((f) => (
            <div className="feat-row" key={`rt-${f.name}`}>
              <div className="feat-row__head">
                <span className="feat-row__name">{f.name}</span>
                <span className="feat-row__src">{titleizeSlug(f.race)}{f.real ? "" : " · narrative"}</span>
              </div>
              <div className="feat-row__desc">{f.desc}</div>
            </div>
          ))}
          {ch.subclass_features?.map((f) => (
            <div className="feat-row" key={`sf-${f.subclass}-${f.name}`}>
              <div className="feat-row__head">
                <span className="feat-row__name">{f.name}</span>
                <span className="feat-row__src">{titleizeSlug(f.subclass)}{f.real ? "" : " · narrative"}</span>
              </div>
              <div className="feat-row__desc">{f.desc}</div>
            </div>
          ))}
          {ch.feats?.map((f) => (
            <div className="feat-row" key={`ft-${f.index}`}>
              <div className="feat-row__head">
                <span className="feat-row__name">{f.name}</span>
                <span className="feat-row__src">Feat</span>
              </div>
              <div className="feat-row__desc">{f.desc}</div>
            </div>
          ))}
        </div>
      )}

      {ch.spells.length > 0 && (
        <div className="sheet__block">
          <div className="label">
            Spells{actions ? " — tap to cast, ⌄ for details" : " — tap for details"}
          </div>
          {Object.entries(spellsByLevel).map(([lvl, entries]) => {
            const lvlNum = Number(lvl);
            const canCast = !actions ? false
              : lvlNum === 0 ? true
              : (ch.spell_slots_used && hasSlotAtOrAbove(ch, lvlNum));
            return (
              <div key={lvl} className="mt8">
                <div style={{ fontSize: 11, color: "var(--gold)", fontWeight: 700, marginBottom: 4 }}>
                  {lvl === "0" ? "Cantrips" : `Level ${lvl}`}
                </div>
                <div className="spell-list">
                  {entries.map((s) => (
                    <SpellRow
                      key={s.index}
                      s={s}
                      canCast={canCast}
                      busy={!!actions?.busy}
                      onCast={actions ? () => actions.onCast(s.index, s.level, s.name) : undefined}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {ch.inventory.length > 0 && (
        <div className="sheet__block">
          <div className="label">Equipment</div>
          {actions ? (
            <div className="inv-list mt8">
              {ch.inventory.map((i) => {
                const consumable = isConsumable(i.name);
                const equippable = !consumable && ["armor", "shield", "weapon", "magic"].includes(i.kind);
                return (
                  <div key={i.index + i.name} className={`inv-row${i.equipped ? " equipped" : ""}`}>
                    {gameId && (
                      <PixelArt
                        shape="square"
                        className="inv-thumb"
                        alt={i.name}
                        body={{ kind: "item", game_id: gameId, name: i.name, item_kind: i.kind }}
                        liveUrl={art[`item:${i.name}`]}
                      />
                    )}
                    <span className="inv-name">
                      {i.name}{i.quantity > 1 ? ` ×${i.quantity}` : ""}
                      {i.equipped && <span className="chip chip--gold" style={{ marginLeft: 6 }}>equipped</span>}
                      {i.attuned && <span className="chip chip--cast" style={{ marginLeft: 4 }}>attuned</span>}
                      {i.magic_bonus && (
                        <span className="inv-magic" title="Magic effect">✦ {i.magic_bonus}</span>
                      )}
                    </span>
                    <span className="inv-actions">
                      {consumable && (
                        <button className="btn btn--sm" disabled={actions.busy}
                          onClick={() => actions.onUseItem(i.index, i.name)}>Use</button>
                      )}
                      {equippable && (
                        <button className="btn btn--sm" disabled={actions.busy}
                          onClick={() => actions.onEquip(i.index, i.name, !i.equipped)}>
                          {i.equipped ? "Unequip" : "Equip"}
                        </button>
                      )}
                      {i.requires_attunement && actions.onAttune && (
                        <button className={`btn btn--sm${i.attuned ? " btn--gold" : ""}`} disabled={actions.busy}
                          title={i.attuned ? "End attunement" : "Attune (max 3)"}
                          onClick={() => actions.onAttune!(i.index, i.name, !i.attuned)}>
                          {i.attuned ? "Unattune" : "Attune"}
                        </button>
                      )}
                      <button className="icon-btn" disabled={actions.busy} title="Drop"
                        onClick={() => actions.onRemoveItem(i.index, i.name)}>✕</button>
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="tag-row mt8">
              {ch.inventory.map((i) => (
                <span key={i.index + i.name} className={`chip${i.equipped ? " chip--gold" : ""}`}
                  title={i.magic_bonus || undefined}>
                  {i.name}{i.quantity > 1 ? ` ×${i.quantity}` : ""}{i.magic_bonus ? " ✦" : ""}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {ch.conditions.length > 0 && (
        <div className="sheet__block">
          <div className="label">Conditions</div>
          <div className="tag-row mt8">
            {ch.conditions.map((c) => (
              <span key={c} className="chip chip--cond">{c}</span>
            ))}
          </div>
        </div>
      )}

      <div className="sheet__block">
        <div className="label">Details</div>
        <div className="detail-grid">
          <div className="detail-row"><span className="dk">Size</span><span className="dv">{ch.size ?? "Medium"}</span></div>
          <div className="detail-row"><span className="dk">Speed</span><span className="dv">{ch.speed ?? 30} ft</span></div>
          {(ch.senses?.darkvision ?? 0) > 0 && (
            <div className="detail-row"><span className="dk">Darkvision</span><span className="dv">{ch.senses?.darkvision} ft</span></div>
          )}
          <div className="detail-row"><span className="dk">Passive Perc.</span><span className="dv">{ch.passive_perception}</span></div>
        </div>
        {(ch.damage_resistances?.length ?? 0) > 0 && (
          <div className="detail-line"><span className="dk">Resistances</span>
            <span className="tag-row">{ch.damage_resistances!.map((r) => <span key={r} className="chip chip--gold">{r}</span>)}</span>
          </div>
        )}
        {(ch.languages?.length ?? 0) > 0 && (
          <div className="detail-line"><span className="dk">Languages</span>
            <span className="dv">{ch.languages!.map(titleizeSlug).join(", ")}</span>
          </div>
        )}
        {((ch.armor_proficiencies?.length ?? 0) > 0 || (ch.weapon_proficiencies?.length ?? 0) > 0) && (
          <div className="detail-line"><span className="dk">Proficiencies</span>
            <span className="dv">{[...(ch.armor_proficiencies ?? []), ...(ch.weapon_proficiencies ?? [])].map(titleizeSlug).join(", ")}</span>
          </div>
        )}
        {ch.currency && (
          <div className="detail-line"><span className="dk">Currency</span>
            <span className="tag-row">
              {(["pp", "gp", "ep", "sp", "cp"] as const).filter((c) => (ch.currency![c] ?? 0) > 0).map((c) => (
                <span key={c} className="chip">{ch.currency![c]} {c}</span>
              ))}
              {(["pp", "gp", "ep", "sp", "cp"] as const).every((c) => (ch.currency![c] ?? 0) === 0) && <span className="dv">—</span>}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// One spell: a cast chip (when interactive) plus a ⌄ toggle that expands the full
// description and casting stats in place. Read-only sheets expand on tapping the name.
function SpellRow({ s, canCast, busy, onCast }: {
  s: SpellEntry;
  canCast: boolean;
  busy: boolean;
  onCast?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const d = s.detail;
  const meta = d
    ? [d.school, d.casting_time, d.range, d.duration, d.concentration ? "Concentration" : null,
       d.ritual ? "Ritual" : null].filter(Boolean).join(" · ")
    : "";
  return (
    <div className={`spell-row${open ? " open" : ""}`}>
      <div className="spell-row__head">
        {onCast ? (
          <button
            className={`spell-row__cast${!canCast ? " disabled" : ""}`}
            disabled={busy || !canCast}
            title={canCast ? `Cast ${s.name}` : "No slot available"}
            onClick={onCast}
          >
            <span className="spell-row__bolt">✦</span>{s.name}
          </button>
        ) : (
          <button className="spell-row__cast spell-row__cast--plain" onClick={() => setOpen((o) => !o)}>
            {s.name}
          </button>
        )}
        {d?.mechanic && <span className="spell-row__mech">{d.mechanic}</span>}
        <button
          className="spell-row__toggle"
          aria-label={open ? "Hide details" : "Show details"}
          onClick={() => setOpen((o) => !o)}
        >
          {open ? "⌃" : "⌄"}
        </button>
      </div>
      {open && d && (
        <div className="spell-row__detail">
          {meta && <div className="spell-row__meta">{meta}{d.components ? ` · ${d.components}` : ""}</div>}
          {d.desc && <div className="spell-row__desc">{d.desc}</div>}
          {d.higher_level && (
            <div className="spell-row__higher"><b>At Higher Levels.</b> {d.higher_level}</div>
          )}
          {d.homebrew && <div className="spell-row__hb">✦ Hearth homebrew</div>}
        </div>
      )}
    </div>
  );
}

// One skill: the usual name/modifier row, tap to expand a short blurb of what it covers.
function SkillRow({ skillKey, label, mod, prof, expert }: {
  skillKey: string;
  label: string;
  mod: number;
  prof: boolean;
  expert: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ab = SKILL_ABILITY[skillKey];
  return (
    <div className={`skill-wrap${open ? " open" : ""}`}>
      <button
        className={`skill-row${prof ? " prof" : ""}${expert ? " expert" : ""}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="sk-dot" />
        <span className="sk-name">
          {label}{ab && <span className="sk-ab"> {(ABILITY_ABBR as Record<string, string>)[ab]}</span>}
        </span>
        <span className="sk-mod">{signed(mod)}</span>
      </button>
      {open && SKILL_DESC[skillKey] && (
        <div className="skill-wrap__desc">
          {SKILL_DESC[skillKey]}
          {expert ? " (expertise)" : prof ? " (proficient)" : ""}
        </div>
      )}
    </div>
  );
}

function groupSpells(ch: FullCharacter): Record<string, SpellEntry[]> {
  const out: Record<string, SpellEntry[]> = {};
  for (const s of [...ch.spells].sort((a, b) => a.level - b.level || a.name.localeCompare(b.name))) {
    const k = String(s.level);
    (out[k] ||= []).push(s);
  }
  return out;
}

function hasSlotAtOrAbove(ch: FullCharacter, level: number): boolean {
  for (const [lvlStr, total] of Object.entries(ch.spell_slots)) {
    const lvl = Number(lvlStr);
    if (lvl < level) continue;
    const used = ch.spell_slots_used[lvlStr] ?? 0;
    if (total - used > 0) return true;
  }
  return false;
}
